import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Search, ShieldCheck, Users } from "lucide-react";
import { getSystemAnalyticsAdmins } from "../../Services/SystemAnalyticsService";
import {
  ADMIN_ROLES,
  SystemAnalyticsAdminRowDTO,
} from "../../Types/SystemAnalyticsType";
import { formatDateTime } from "../../../Shared/Utils/HelperUtils";
import Pagination from "../../../Shared/Pagination/Pagination";
import { PaginationResponse } from "../../../Shared/Pagination/PaginationResponse";
import {
  EmptyState,
  fieldClass,
  headerClass,
  LoadingRows,
} from "./SmartAnalyticsShared";

const PAGE_SIZE = 10;

const roleBadge = (role: string) => {
  const superAdmin = role?.toUpperCase() === "SUPER_ADMIN";
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
        superAdmin
          ? "border border-amber-500/30 bg-amber-500/10 text-amber-400"
          : "border border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
      }`}
    >
      {superAdmin ? "Super admin" : "Delegated"}
    </span>
  );
};

interface AdminListComponentProps {
  refreshKey?: number;
}

const AdminListComponent = ({ refreshKey }: AdminListComponentProps) => {
  const [admins, setAdmins] = useState<PaginationResponse<SystemAnalyticsAdminRowDTO> | null>(null);
  const [adminSearch, setAdminSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [adminPage, setAdminPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const roleOptions = useMemo(() => ADMIN_ROLES as unknown as string[], []);

  const loadAdmins = useCallback(async () => {
    const response = await getSystemAnalyticsAdmins(adminSearch, roleFilter, adminPage, PAGE_SIZE);
    if (response.success) {
      setAdmins(response.data);
    }
  }, [adminSearch, roleFilter, adminPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAdmins().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadAdmins, refreshKey]);

  const applyAdminSearch = (value: string) => {
    setAdminSearch(value);
    setAdminPage(0);
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
      <div className="flex flex-wrap items-center justify-between gap-3 p-6">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
            <Users className="h-4 w-4 text-cyan-400" />
            All admins
          </h2>
          <p className="mt-1 flex items-center gap-1.5 text-[10px] font-semibold text-slate-500">
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
            Read-only. Candidates and recruiters are excluded.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
            <input
              value={adminSearch}
              onChange={(event) => applyAdminSearch(event.target.value)}
              placeholder="Search admins"
              className={`${fieldClass} w-56 pl-9`}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(event) => {
              setRoleFilter(event.target.value);
              setAdminPage(0);
            }}
            className={`${fieldClass} w-44`}
          >
            <option value="">All admin roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role === "SUPER_ADMIN" ? "Super admins" : role.replace(/_/g, " ").toLowerCase()}
              </option>
            ))}
          </select>
        </div>
      </div>

      <table className="w-full">
        <thead className="border-y border-slate-800 bg-slate-900/60">
          <tr>
            <th className={`${headerClass} text-left`}>Admin</th>
            <th className={`${headerClass} text-left`}>Role</th>
            <th className={`${headerClass} text-left`}>Status</th>
            <th className={`${headerClass} text-left`}>Notes written</th>
            <th className={`${headerClass} text-left`}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRows rows={4} cols={5} />
          ) : !admins || admins.content.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <EmptyState
                  icon={<Users className="h-6 w-6" />}
                  title="No admins match"
                  body="No admin accounts match this search. Candidates and recruiters never appear in this list."
                />
              </td>
            </tr>
          ) : (
            admins.content.map((admin: SystemAnalyticsAdminRowDTO) => (
              <tr key={admin.userId} className="border-t border-slate-800/70 transition hover:bg-slate-900/40">
                <td className="px-5 py-4">
                  <p className="text-xs font-bold text-slate-200">{admin.fullName || admin.email}</p>
                  <p className="text-[10px] font-semibold text-slate-500">{admin.email}</p>
                </td>
                <td className="px-5 py-4">{roleBadge(admin.role)}</td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                  {admin.userStatus || "Unknown"}
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-300">
                  {(admin.noteCount ?? 0).toLocaleString()}
                </td>
                <td className="px-5 py-4">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                    {formatDateTime(admin.createdAt ?? undefined)}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {admins && admins.totalPages > 1 && <Pagination {...admins} setPage={setAdminPage} />}
    </div>
  );
};

export default AdminListComponent;
